/**
 * useIdleLoopUrl — Feb 2026, reworked Oct 2026
 * ============================================
 *
 * Fetches the seamless idle-loop-video URL for a given AvA (used by
 * the chat portrait + AvA library tiles).
 *
 * Oct 2026 — lazy generation support: a freshly saved AvA has its loop
 * rendered on first open (~60-90s). The backend reports `generating:true`
 * while the render runs, so this hook now:
 *   - only caches SUCCESSFUL responses (a null must never stick for 10 min)
 *   - polls every 15s (max 20 tries ≈ 5 min) while a render is in flight
 */
import { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import CacheManager from '../utils/cacheManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const IDLE_LOOP_TTL_MS = 10 * 60 * 1000;

export const useIdleLoopUrl = (avaId) => {
  const [idleLoopUrl, setIdleLoopUrl] = useState(null);

  useEffect(() => {
    if (!avaId) {
      setIdleLoopUrl(null);
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    let attempts = 0;
    const cacheKey = `idle_loop_${avaId}`;

    const load = async () => {
      try {
        const cached = CacheManager.get(cacheKey);
        const data = cached || await apiClient.request(`/api/ava-video/idle-loop-for-ava/${avaId}`);
        if (cancelled) return;
        if (data?.idle_loop_url) {
          if (!cached) CacheManager.set(cacheKey, data, IDLE_LOOP_TTL_MS);
          setIdleLoopUrl(`${API_URL}/api/files/${data.idle_loop_url}`);
        } else {
          setIdleLoopUrl(null);
          if (data?.generating && attempts < 20) {
            attempts += 1;
            timer = setTimeout(load, 15000);
          }
        }
      } catch {
        if (!cancelled) setIdleLoopUrl(null);
      }
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [avaId]);

  return idleLoopUrl;
};

export default useIdleLoopUrl;
